import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

class CartDrawerRecommendations extends HTMLElement {
  connectedCallback() {
    this.#bindControls();
    this.#bindQuickAdd();
    this.#loadRecommendations();
  }

  #bindControls() {
    const track = this.querySelector('[data-upsell-track]');
    const prev = this.querySelector('[data-upsell-prev]');
    const next = this.querySelector('[data-upsell-next]');

    if (!track || !prev || !next) return;

    prev.addEventListener('click', () => {
      track.scrollBy({ left: -track.clientWidth * 0.8, behavior: 'smooth' });
    });

    next.addEventListener('click', () => {
      track.scrollBy({ left: track.clientWidth * 0.8, behavior: 'smooth' });
    });
  }

  #bindQuickAdd() {
    this.addEventListener('click', async (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-upsell-add], [data-atc-button]') : null;
      if (!(button instanceof HTMLButtonElement)) return;

      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;

      button.disabled = true;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionIds.push(item.dataset.sectionId);
        }
      });

      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: sectionIds.join(','),
          }),
        });

        const data = await response.json();
        if (data.status) {
          throw new Error(data.description || data.message || 'Failed to add upsell item');
        }

        this.dispatchEvent(
          new CartAddEvent(data ?? undefined, 'cart-drawer-upsell', {
            source: 'cart-drawer-upsell',
            itemCount: 1,
            sections: data.sections,
            variantId: String(variantId),
          })
        );
      } catch (error) {
        console.error('[cart-drawer-upsell] Add to cart failed', error);
      } finally {
        button.disabled = false;
      }
    });
  }

  async #loadRecommendations() {
    if (this.dataset.source !== 'recommendations') return;

    const productId = this.dataset.productId;
    const limit = this.dataset.limit || '6';
    const recommendationsUrl = this.dataset.productRecommendationsUrl;
    if (!productId || !recommendationsUrl) return;

    const track = this.querySelector('[data-upsell-track]');
    if (!track) return;

    try {
      const url = new URL(recommendationsUrl, window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', limit);
      url.searchParams.set('intent', 'related');

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

      const payload = await response.json();
      const products = Array.isArray(payload?.products) ? payload.products : [];
      if (!products.length) return;

      const availableProducts = products.filter(
        (product) => product?.available && Array.isArray(product.variants) && product.variants.some((variant) => variant.available)
      );

      if (!availableProducts.length) return;

      const locale = this.dataset.locale || 'en-US';
      const currencyCode = this.dataset.currencyCode || (payload?.products?.[0]?.price_range?.min_variant_price?.currency_code ?? 'USD');
      const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode });

      const html = availableProducts
        .slice(0, Number(limit))
        .map((product) => {
          const firstVariant = product.variants.find((variant) => variant.available);
          if (!firstVariant) return '';

          const image = product.featured_image
            ? `<img class="cart-drawer__upsell-image" src="${product.featured_image}" alt="${this.#escape(product.title)}" loading="lazy">`
            : '';

          return `
            <article class="product-teaser-card cart-drawer__upsell-card" data-product-id="${product.id}">
              <a class="product-teaser-card__image-link" href="${product.url}" aria-label="${this.#escape(product.title)}">
                <div class="product-teaser-card__image-wrap">${image}</div>
              </a>
              <div class="product-teaser-card__content">
                <a class="product-teaser-card__title-link" href="${product.url}">
                  <h3 class="product-teaser-card__title">${this.#escape(product.title)}</h3>
                </a>
                <div class="product-teaser-card__footer">
                  <div class="product-teaser-card__prices">
                    <p class="product-teaser-card__price">${formatter.format((firstVariant.price || 0) / 100)}</p>
                  </div>
                  <button
                    type="button"
                    class="product-teaser-card__atc"
                    data-atc-button
                    data-atc-behavior="cart-drawer"
                    data-variant-id="${firstVariant.id}"
                    aria-label="Add ${this.#escape(product.title)} to cart"
                  >
                    <span class="product-teaser-card__atc-icon product-teaser-card__atc-icon--default" aria-hidden="true">+</span>
                    <span class="product-teaser-card__atc-icon product-teaser-card__atc-icon--added" aria-hidden="true">+</span>
                  </button>
                </div>
              </div>
            </article>
          `;
        })
        .join('');

      if (html.trim()) {
        track.innerHTML = html;
      }
    } catch (error) {
      console.error('[cart-drawer-upsell] Failed to load recommendations', error);
    }
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escape(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
